import React from 'react';
import qs from 'qs';

import { isProduction } from '../common/environment';
import { EVENT, EXPERIMENT, ROLLOUT, VARIABLE } from '../constants';
import { getDomainFamily } from '@/common/domains';

export function getLastSegment(url: string): string {
  if (!url) {
    return '';
  }
  return url.split('/').pop() ?? '';
}

// Transform key value map to encoded url query string
export function toQueryString(
  map:
    | {
        [key: string]:
          | number
          | string
          | boolean
          | Array<string | number | boolean>;
      }
    | undefined,
): string {
  if (map == undefined) {
    return '';
  }
  const u = encodeURIComponent;
  const queryStringList = Object.keys(map)
    // filter empty values
    .filter((key) => map[key] === 0 || map[key])
    // making query string
    .reduce((queryStringParts: Array<string>, key) => {
      const value = map[key];

      if (Array.isArray(value)) {
        value.forEach((element) => {
          queryStringParts.push(`${u(key)}=${u(String(element))}`);
        });
      } else {
        queryStringParts.push(`${u(key)}=${u(String(value))}`);
      }

      return queryStringParts;
    }, []);

  const queryString =
    queryStringList.length === 0 ? '' : `?${queryStringList.join('&')}`;

  return queryString;
}

const ACL_WIKI_URL = 'https://go.grab.com/grab-xportal-aclguide';
const STG_CONCEDO_ID = 'dd9975adc1a04f83b73326768fbd4049';
const PRD_CONCEDO_ID = '2f2b605ac80e4595b8623027b6302f23';

export const GEO_TOOLS_GRAB_AREA_PRD_URL =
  'https://geo-tools.myteksi.net/ops-tools/grab-area';
export const GEO_TOOLS_GRAB_AREA_STG_URL =
  'https://geo-tools.stg-myteksi.com/ops-tools/grab-area';

export const GEO_TOOLS_GRAB_AREA_URL = isProduction
  ? GEO_TOOLS_GRAB_AREA_PRD_URL
  : GEO_TOOLS_GRAB_AREA_STG_URL;

export const JIRA_URL = 'https://grabtaxi.atlassian.net/browse/';

export const EXPERIMENT_SAMPLE_TEMPLATE_URL =
  'https://docs.google.com/presentation/d/1L1TEmgy4usa7MqaPTLznaXoIh1Qv6PyvzGL-pmYKoAo/edit#slide=id.g6ad9d648e8_0_65';

const CONCEDO_STG_URL = `https://concedo.stg-myteksi.com/new-request?appID=${STG_CONCEDO_ID}&applyingFor=SELF`;
const CONCEDO_PROD_URL = `https://concedo.grab.com/new-request?appID=${PRD_CONCEDO_ID}&applyingFor=SELF`;
export const CONCEDO_REQUEST_URL = isProduction
  ? CONCEDO_PROD_URL
  : CONCEDO_STG_URL;
const CONCEDO_REQUEST_PAGE = (
  <a href={CONCEDO_REQUEST_URL} target="_blank" rel="noreferrer">
    here
  </a>
);
const ACCESS_CONTROL_WIKI = (
  <a href={ACL_WIKI_URL} target="_blank" rel="noreferrer">
    here
  </a>
);
const USER_ROLES_PAGE = (
  <a href="/t/user-roles" target="_blank">
    here
  </a>
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function formatHasAccessErrorGeneric(error: any) {
  const message: string = error?.response?.data?.arg;
  return (
    <span>
      <strong>Error Message</strong>
      <br />
      {message}
      <br />
      <i>Note: Scope means Tech Family</i>
      <br />
      <strong>How to Resolve</strong>
      <br />
      Check personal permissions: {USER_ROLES_PAGE}
      <br />
      Where to raise request: {CONCEDO_REQUEST_PAGE}
      <br />
      Detailed guide: {ACCESS_CONTROL_WIKI}
    </span>
  );
}

// TODO: To deprecate this function. Currently still used for an edge case in the access control page
export function formatHasAccessError(type: string, showPublisherFlag: boolean) {
  let rolloutExperimentModerator = (
    <span>
      <strong>RolloutModerator</strong> OR <strong>ExperimentModerator</strong>
    </span>
  );
  let rolloutModerator = <strong>RolloutModerator</strong>;
  let experimentModerator = <strong>ExperimentModerator</strong>;
  const publisher = (
    <span>
      <strong>Publisher</strong>
    </span>
  );
  if (showPublisherFlag) {
    rolloutExperimentModerator = publisher;
    rolloutModerator = publisher;
    experimentModerator = publisher;
  }
  switch (type) {
    case VARIABLE:
      return (
        <span>
          No permission. Please raise a request for <strong>Editor</strong> OR{' '}
          {rolloutExperimentModerator} based on variable&apos;s TF{' '}
          {CONCEDO_REQUEST_PAGE} and get your manager&apos;s approval.
          Step-by-step guide {ACCESS_CONTROL_WIKI}.
        </span>
      );
    case ROLLOUT:
      return (
        <span>
          No permission. Please raise a request for {rolloutModerator} based on
          variable&apos;s TF {CONCEDO_REQUEST_PAGE} and get your manager&apos;s
          approval. Step-by-step guide {ACCESS_CONTROL_WIKI}.
        </span>
      );
    case EXPERIMENT:
      return (
        <span>
          No permission. Please raise a request for {experimentModerator} based
          on variable&apos;s TF {CONCEDO_REQUEST_PAGE} and get your
          manager&apos;s approval. Step-by-step guide {ACCESS_CONTROL_WIKI}.
        </span>
      );
    case EVENT:
      return (
        <span>
          No permission. Please raise a request for <strong>Editor</strong> OR{' '}
          {rolloutExperimentModerator} based on variable&apos;s TF{' '}
          {CONCEDO_REQUEST_PAGE} and get your manager&apos;s approval.
          Step-by-step guide {ACCESS_CONTROL_WIKI}.
        </span>
      );
    default:
      return <span>No permission. Please contact GrabX for support.</span>;
  }
}

export function generatePath(
  pattern: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: { [key: string]: any } = null,
) {
  if (!pattern) return '';

  if (!params) return pattern;

  const paths = pattern.split('/');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function validateAllowedValue(allowedValues: any[]): boolean {
    if (!allowedValues?.length) return true;

    for (let i = 0; i < allowedValues.length; i += 1) {
      const value = allowedValues[i];

      if (!new RegExp('([a-zA-Z\\d])').test(value))
        throw `Invalid value: ${value}. Only accepts alphanumeric`;
    }
  }

  paths.forEach((path: string, index: number) => {
    if (!path.startsWith(':')) return false;

    let allowedValuesStr: string = '';
    let allowedValues = [];

    if (path.includes('(') && path.endsWith(')')) {
      allowedValuesStr = path.substring(path.indexOf('(') + 1, path.length - 1);

      if (allowedValuesStr) allowedValues = allowedValuesStr.split('|');

      validateAllowedValue(allowedValues);
    }

    const paramKey = path
      .replace(allowedValuesStr, '')
      .replace('()', '')
      .substring(1);

    if (!Object.prototype.hasOwnProperty.call(params, paramKey))
      throw `TypeError: Expected "${paramKey}" to be defined`;

    const paramValue = params?.[paramKey];

    if (allowedValues?.length && !allowedValues.includes(paramValue))
      throw `Invalid value provided for "${paramKey}". Values allowed: ${allowedValues.join(
        ', ',
      )}`;

    paths[index] = paramValue;
  });

  return paths.join('/');
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractParamValueByName(search: string, name: string): any {
  const urlParams = new URLSearchParams(search);
  const entries = urlParams.entries();
  const filtersObj = {};

  for (const [key, value] of entries) {
    filtersObj[key] = value;
  }

  return filtersObj?.[name];
}

export function extractFiltersFromSearchParams(
  search: string,
  blacklist: string[] = [],
) {
  const urlParams = new URLSearchParams(search);
  const entries = urlParams.entries();
  const filtersObj = {};

  for (const [key, value] of entries) {
    if (!Object.prototype.hasOwnProperty.call(filtersObj, key)) {
      filtersObj[key] = value;

      continue;
    }

    if (Array.isArray(filtersObj[key])) {
      filtersObj[key].push(value);

      continue;
    }

    filtersObj[key] = [filtersObj[key], value];
  }

  if (blacklist?.length) {
    blacklist.forEach((key) => delete filtersObj[key]);
  }

  return filtersObj;
}

export function getUrlWithParams(
  url: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: { [key: string]: any },
): string {
  if (!url) return '';

  if (!Object.keys(params)?.length) return url;

  return `${url}?${parseObjectToParams(params)}`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getSearchParamsObject(value: string): { [key: string]: any } {
  if (!value) return {};

  try {
    const url = new URL(value);
    const queryString = url.search;
    return qs.parse(queryString, { ignoreQueryPrefix: true });
  } catch (_) {
    return qs.parse(value, { ignoreQueryPrefix: true });
  }
}

export function parseObjectToParams(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: { [key: string]: any },
  options = {},
): string {
  return qs.stringify(params, {
    ...options,
    arrayFormat: 'repeat',
  });
}

export function getUrlBasedOffOriginAndDomainFamily(path: string): string {
  const origin = window.location.origin;

  if (!path) return origin;

  const prefixPath = `${origin}/${getDomainFamily()}`;

  if (path.startsWith('/')) return `${prefixPath}${path}`;

  return `${prefixPath}/${path}`;
}

export function notFoundRedirection(): void {
  const { protocol, host } = window.location;
  window.location.href = `${protocol}//${host}/${getDomainFamily()}/not-found`;
}

export const buildUrlWithParams = (
  url: string,
  params?: { [key: string]: string },
) => {
  const urlParams = new URLSearchParams(params);
  return `${url}?${urlParams.toString()}`;
};
